import { Link } from 'react-router-dom';
import { trustedPartnerDetailPath } from '@/lib/constants/routes';
import type { TrustedPartnerSummaryDto } from '@/lib/types/events.types';
import { RESOURCES_STRINGS } from '../constants/resources-strings';

interface ResourcePartnerCreditProps {
  /** The resolved partner. The caller renders nothing when the resource credits nobody. */
  partner: TrustedPartnerSummaryDto;
  /** `sm` for cards, `md` for the detail dialog. */
  size?: 'sm' | 'md';
}

const LOGO_SIZE = {
  sm: 'max-h-6',
  md: 'max-h-9',
} as const;

/**
 * "Provided by [Partner]" with the partner's logo (FR-278, MYN-189).
 *
 * One component for all three surfaces the client asked for - the resource
 * card, the featured card and the detail view (A-299) - so the credit cannot
 * read three different ways. Size is the only thing that varies.
 *
 * Unlike the event credit, the NAME is shown as well as the logo: the client
 * asked for "Provided by [Partner]" in words here, where events show the logo
 * alone.
 *
 * Resources are member-only, so the profile link is always safe to render -
 * there is no logged-out reader to send to a login wall, which is why this
 * needs none of the auth branching `EventTrustedPartner` carries.
 */
export function ResourcePartnerCredit({ partner, size = 'sm' }: ResourcePartnerCreditProps) {
  return (
    <Link
      to={trustedPartnerDetailPath(partner.entityId)}
      className="text-text-muted hover:text-text-secondary inline-flex cursor-pointer items-center gap-2 transition-colors"
      // The card is itself clickable on some surfaces; this credit is its own
      // destination, so the outer handler must not swallow it.
      onClick={(event) => event.stopPropagation()}
    >
      <img
        src={partner.logoUrl}
        alt={partner.name}
        className={`${LOGO_SIZE[size]} w-auto max-w-24 object-contain`}
      />
      <span className="text-p6">
        {RESOURCES_STRINGS.PROVIDED_BY} {partner.name}
      </span>
    </Link>
  );
}
